import { ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import { debounce } from 'lodash'

export function useContactsFilters(initialFilters = {}) {
    const search = ref(initialFilters.search ?? '')
    const tags = ref(initialFilters.tags ?? [])
    const formIds = ref(initialFilters.form_ids ?? [])
    const dateFrom = ref(initialFilters.date_from ?? '')
    const dateTo = ref(initialFilters.date_to ?? '')
    const activityFrom = ref(initialFilters.activity_from ?? '')
    const activityTo = ref(initialFilters.activity_to ?? '')
    const sortBy = ref(initialFilters.sort_by ?? 'created_at')
    const sortDir = ref(initialFilters.sort_dir ?? 'desc')
    const perPage = ref(initialFilters.per_page ?? 50)

    function getParams(overrides = {}) {
        return {
            search: search.value || undefined,
            tags: tags.value.length ? tags.value : undefined,
            form_ids: formIds.value.length ? formIds.value : undefined,
            date_from: dateFrom.value || undefined,
            date_to: dateTo.value || undefined,
            activity_from: activityFrom.value || undefined,
            activity_to: activityTo.value || undefined,
            sort_by: sortBy.value !== 'created_at' ? sortBy.value : undefined,
            sort_dir: sortDir.value !== 'desc' ? sortDir.value : undefined,
            per_page: perPage.value !== 50 ? perPage.value : undefined,
            page: undefined,
            ...overrides,
        }
    }

    function visit(params) {
        router.visit('/app/contacts', {
            data: params,
            preserveState: true,
            preserveScroll: true,
            replace: true,
        })
    }

    const debouncedSearch = debounce(() => visit(getParams()), 350)

    watch(search, debouncedSearch)

    function applyFilters(overrides = {}) {
        visit(getParams(overrides))
    }

    function toggleTag(tag) {
        const idx = tags.value.indexOf(tag)
        if (idx === -1) {
            tags.value = [...tags.value, tag]
        } else {
            tags.value = tags.value.filter((t) => t !== tag)
        }
        applyFilters()
    }

    function toggleFormId(id) {
        const idx = formIds.value.indexOf(id)
        if (idx === -1) {
            formIds.value = [...formIds.value, id]
        } else {
            formIds.value = formIds.value.filter((f) => f !== id)
        }
        applyFilters()
    }

    function setSort(column) {
        if (sortBy.value === column) {
            sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
        } else {
            sortBy.value = column
            sortDir.value = 'asc'
        }
        applyFilters()
    }

    function setPage(page) {
        visit(getParams({ page }))
    }

    function resetFilters() {
        search.value = ''
        tags.value = []
        formIds.value = []
        dateFrom.value = ''
        dateTo.value = ''
        activityFrom.value = ''
        activityTo.value = ''
        sortBy.value = 'created_at'
        sortDir.value = 'desc'
        perPage.value = 50
        visit({})
    }

    function hasActiveFilters() {
        return !!(
            search.value ||
            tags.value.length ||
            formIds.value.length ||
            dateFrom.value ||
            dateTo.value ||
            activityFrom.value ||
            activityTo.value
        )
    }

    return {
        search, tags, formIds,
        dateFrom, dateTo, activityFrom, activityTo,
        sortBy, sortDir, perPage,
        applyFilters, toggleTag, toggleFormId,
        setSort, setPage, resetFilters, hasActiveFilters,
    }
}
